import React, { useState, useEffect, useContext } from "react";
import { Box, IconButton } from "@mui/material";
import { EcUserDataContext } from "@eclear-ag/ec-common-ui";
import { DragIcon } from "../../assets/icons/SelectIcon";
import { canAccessClearCustoms } from "../../pages/ClearCustoms/tools/tool";

export const DND = (props) => {
  const { defaultComponents } = props;
  const { userData } = useContext(EcUserDataContext);
  const isSubscribedToClearCustoms = canAccessClearCustoms({ userData });

  const [components, setComponents] = useState(defaultComponents);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    const savedOrder = localStorage.getItem("clearCustomsComponentOrder");
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder);
        const reorderedComponents = parsedOrder
          .map((savedItem) =>
            defaultComponents.find((comp) => comp.id === savedItem.id)
          )
          .filter(Boolean);

        if (reorderedComponents.length === defaultComponents.length) {
          setComponents(reorderedComponents);
        }
      } catch (error) {
        console.error(
          "Error loading component order from localStorage:",
          error
        );
      }
    }
  }, []);

  const saveComponentOrder = (newComponents) => {
    try {
      const orderToSave = newComponents.map((comp) => ({
        id: comp.id,
        name: comp.name
      }));
      localStorage.setItem(
        "clearCustomsComponentOrder",
        JSON.stringify(orderToSave)
      );
    } catch (error) {
      console.error("Error saving component order to localStorage:", error);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const newComponents = [...components];
    const draggedComponent = newComponents[draggedItem];

    newComponents.splice(draggedItem, 1);
    newComponents.splice(dropIndex, 0, draggedComponent);

    setComponents(newComponents);
    saveComponentOrder(newComponents);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // // Optional: Reset to default order function
  // const resetToDefaultOrder = () => {
  //   setComponents(defaultComponents);
  //   localStorage.removeItem("clearCustomsComponentOrder");
  // };

  if (!isSubscribedToClearCustoms) return null;

  return (
    <>
      {components.map((item, index) => {
        const Component = item.component;
        const isDragging = draggedItem === index;
        const isDropTarget = dragOverIndex === index;

        return (
          <React.Fragment key={item.id}>
            {Component && (
              <Box
                data-testid={`${item.id}_box`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: isDropTarget ? 0.4 : isDragging ? 0.6 : 1,
                  transition: "all 0.2s ease",
                  borderRadius: "8px",
                  backgroundColor: isDropTarget
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  padding: isDropTarget ? "8px" : "0"
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Component {...props} />
                </Box>

                <IconButton
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: "grab",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.06)"
                    },
                    "&:active": {
                      cursor: "grabbing"
                    }
                  }}
                >
                  <DragIcon />
                </IconButton>
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
